import { z } from 'zod';
import { ehrProviderSchema, ehrLocationSchema, ehrTimeSlotSchema } from './provider';
import { ehrPatientDataSchema } from './patient';
import { ehrAppointmentSchema } from './appointment';
import { EHR_ERROR_CODES, ehrErrorDefinition, type EhrErrorCode } from './errors';

// =============================================================================
// Connector Error
// =============================================================================

/**
 * A connector failure on the wire. `code` is a logical code from the EHR error
 * registry (`EHR_ERRORS`, ADR-251) and `message` is its standard, EHR-neutral text.
 * The EHR's own wording goes in `detail` — for logs and support, never shown as the
 * message. `code` stays a string on the wire so an unregistered code from an older
 * connector still parses; readers resolve it with `ehrErrorDefinition`.
 */
export const connectorErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  retryable: z.boolean(),
  retryAfterSeconds: z.number().optional(),
  /** The EHR's original error text, when there is one. */
  detail: z.string().optional(),
  /**
   * Fields the EHR said were missing or invalid. A patient field is named with its
   * standard name (`EHR_PATIENT_FIELD`, ADR-282), not the EHR's own.
   */
  fields: z.array(z.string()).optional(),
  details: z.unknown().optional(),
});

export type ConnectorError = z.infer<typeof connectorErrorSchema>;

// =============================================================================
// Connector Slot Schema (needed by both NDJSON records and standalone response)
// =============================================================================

export const connectorSlotSchema = z.object({
  startTime: z.string(),
  endTime: z.string(),
  providerId: z.string(),
  providerName: z.string(),
  locationId: z.string(),
  locationName: z.string(),
  /**
   * EHR appointment-type id this slot was offered under, when the source is
   * type-scoped (athena returns open slots per appointment type). Omitted by
   * type-agnostic sources (the Office Ally calendar grid carries no type). The
   * caller maps this to its internal appointment-type id when persisting.
   */
  appointmentTypeId: z.string().optional(),
  /**
   * The EHR's own id for this open slot, when it has one (athena's
   * `appointmentid`). Booking pins it, so the EHR refuses the slot once it is
   * taken instead of opening a second one at the same time (ADR-273). Omitted by
   * sources with no slot identity (the Office Ally grid).
   */
  ehrSlotId: z.string().optional(),
  /**
   * The slot's OWN appointment type in the EHR, when it differs from the type it
   * was offered under (`appointmentTypeId`): a generic slot ("Any 15") offered for
   * a specific type. Lets the caller learn which types each generic type accepts,
   * so a change-feed record for a generic slot needs no re-read (ADR-300).
   */
  slotAppointmentTypeId: z.string().optional(),
});

export type ConnectorSlot = z.infer<typeof connectorSlotSchema>;

// =============================================================================
// NDJSON Stream Record Types
// =============================================================================

export const syncProviderRecordSchema = z.object({
  type: z.literal('provider'),
  data: ehrProviderSchema,
});

export const syncLocationRecordSchema = z.object({
  type: z.literal('location'),
  data: ehrLocationSchema,
});

export const syncPatientRecordSchema = z.object({
  type: z.literal('patient'),
  data: ehrPatientDataSchema,
});

export const syncAppointmentRecordSchema = z.object({
  type: z.literal('appointment'),
  data: ehrAppointmentSchema,
});

export const syncSlotRecordSchema = z.object({
  type: z.literal('slot'),
  data: z.array(connectorSlotSchema),
});

export const syncErrorRecordSchema = z.object({
  type: z.literal('error'),
  error: connectorErrorSchema,
});

export const syncCompleteMetadataSchema = z.object({
  syncStartedAt: z.string(),
  syncCompletedAt: z.string(),
  counts: z.object({
    providers: z.number(),
    locations: z.number(),
    patients: z.number(),
    appointments: z.number(),
    slots: z.number(),
    errors: z.number(),
  }),
  resumeCursor: z.string().optional(),
});

export const syncCompleteRecordSchema = z.object({
  type: z.literal('sync_complete'),
  metadata: syncCompleteMetadataSchema,
});

export const syncRecordSchema = z.discriminatedUnion('type', [
  syncProviderRecordSchema,
  syncLocationRecordSchema,
  syncPatientRecordSchema,
  syncAppointmentRecordSchema,
  syncSlotRecordSchema,
  syncErrorRecordSchema,
  syncCompleteRecordSchema,
]);

export type SyncProviderRecord = z.infer<typeof syncProviderRecordSchema>;
export type SyncLocationRecord = z.infer<typeof syncLocationRecordSchema>;
export type SyncPatientRecord = z.infer<typeof syncPatientRecordSchema>;
export type SyncAppointmentRecord = z.infer<typeof syncAppointmentRecordSchema>;
export type SyncSlotRecord = z.infer<typeof syncSlotRecordSchema>;
export type SyncErrorRecord = z.infer<typeof syncErrorRecordSchema>;
export type SyncCompleteRecord = z.infer<typeof syncCompleteRecordSchema>;
export type SyncCompleteMetadata = z.infer<typeof syncCompleteMetadataSchema>;
export type SyncRecord = z.infer<typeof syncRecordSchema>;

// =============================================================================
// Available Slots Response
// =============================================================================

export const availableSlotsResponseSchema = z.object({
  data: z.object({
    slots: z.array(connectorSlotSchema),
    providers: z.array(ehrProviderSchema),
    locations: z.array(ehrLocationSchema),
  }),
  metadata: z.object({
    dateRange: z.object({
      start: z.string(),
      end: z.string(),
    }),
    totalSlots: z.number(),
  }),
});

export type AvailableSlotsResponse = z.infer<typeof availableSlotsResponseSchema>;

// =============================================================================
// Slot changes (the EHR's change feed, ADR-273 phase 3)
// =============================================================================

/**
 * One open slot whose state changed in the EHR, in its LATEST state (athena's
 * `/appointments/changed` holds one snapshot per appointment, not an event log).
 *   - `open`   — bookable: added, reopened by a cancel/reschedule, or unfrozen.
 *   - `frozen` — still exists but can't be booked.
 *   - `taken`  — no longer open: booked, cancelled, checked in or out.
 * Times are the department's wall clock (no offset); the caller resolves them in
 * the location's time zone. A deleted or retyped slot never appears: the EHR does
 * not report those (tested 2026-09-25 and 2026-09-30), which is why open slots are
 * re-read weekly.
 */
export const slotChangeSchema = z.object({
  ehrSlotId: z.string(),
  state: z.enum(['open', 'frozen', 'taken']),
  startTime: z.string(),
  endTime: z.string(),
  providerId: z.string(),
  locationId: z.string(),
  /** The slot's own appointment type, when it has one. */
  appointmentTypeId: z.string().optional(),
});

export type SlotChange = z.infer<typeof slotChangeSchema>;

/**
 * One read of the EHR's appointment change feed (ADR-300): the slot changes, and the
 * patient and appointment records for every booked, cancelled, checked-in or
 * checked-out appointment (patients first, as in a sync batch). `readAt` is when the
 * read began: passing it back as `replayFrom` re-delivers this read.
 * `not_subscribed` — the EHR has no subscription for the feed; nothing was read.
 */
export const scheduleChangesResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('changes'),
    slots: z.array(slotChangeSchema),
    records: z.array(
      z.discriminatedUnion('type', [syncPatientRecordSchema, syncAppointmentRecordSchema])
    ),
    /** Records the connector read but couldn't map: consumed, so they count as failed. */
    unmappedRecords: z.number().int().nonnegative().optional(),
    readAt: z.string(),
  }),
  z.object({ status: z.literal('not_subscribed') }),
]);

export type ScheduleChangesResponse = z.infer<typeof scheduleChangesResponseSchema>;

/**
 * One patient changed in the EHR, in its latest state.
 *   - `updated` — added or changed; `patient` is its current record.
 *   - `deleted` — the EHR deleted the chart.
 * `mergedFromEhrPatientIds` names the charts the EHR merged INTO this one.
 */
export const patientChangeSchema = z.object({
  kind: z.enum(['updated', 'deleted']),
  ehrPatientId: z.string(),
  patient: ehrPatientDataSchema.optional(),
  mergedFromEhrPatientIds: z.array(z.string()),
});

export type PatientChange = z.infer<typeof patientChangeSchema>;

/** One read of the EHR's patient change feed (ADR-300); see {@link scheduleChangesResponseSchema}. */
export const patientChangesResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('changes'),
    changes: z.array(patientChangeSchema),
    readAt: z.string(),
  }),
  z.object({ status: z.literal('not_subscribed') }),
]);

export type PatientChangesResponse = z.infer<typeof patientChangesResponseSchema>;

// =============================================================================
// Health Check Response
// =============================================================================

export const connectorHealthResponseSchema = z.object({
  status: z.enum(['ok', 'degraded', 'error']),
  ehrConnection: z.enum(['connected', 'disconnected', 'error']),
  activeSessions: z.number(),
  timestamp: z.string(),
});

export type ConnectorHealthResponse = z.infer<typeof connectorHealthResponseSchema>;

// =============================================================================
// Change-feed subscriptions (ADR-299)
// =============================================================================

/**
 * The EHR change feeds VagusX follows. A feed is a queue of changed records the
 * EHR fills only while a subscription for it exists (athena:
 * `/appointments/changed`, `/patients/changed`).
 */
export const CHANGE_FEEDS = ['appointments', 'patients'] as const;
export const changeFeedSchema = z.enum(CHANGE_FEEDS);
export type ChangeFeed = z.infer<typeof changeFeedSchema>;

/**
 * A feed's subscription as the EHR reports it.
 *   - `active`   — subscribed to every event the feed carries by default.
 *   - `partial`  — subscribed to only some of them: some changes will not arrive.
 *   - `inactive` — not subscribed: the feed stays empty.
 *   - `unknown`  — the EHR answered but could not say.
 * `events` names what the EHR says is subscribed. An EHR applies a subscribe or
 * unsubscribe some time after it is requested (athena: 10–20 minutes) and reports
 * the old state until then.
 */
export const changeFeedSubscriptionSchema = z.object({
  status: z.enum(['active', 'partial', 'inactive', 'unknown']),
  events: z.array(z.string()),
});
export type ChangeFeedSubscription = z.infer<typeof changeFeedSubscriptionSchema>;

// =============================================================================
// Error Taxonomy Codes
// =============================================================================

/**
 * Every logical error code (the EHR error registry, ADR-251). Kept under its
 * original name so existing call sites read the same; the registry in `errors.ts`
 * is the source of truth.
 */
export const CONNECTOR_ERROR_CODES = EHR_ERROR_CODES;

export type ConnectorErrorCode = EhrErrorCode;

/**
 * Build a connector error for a registered code: the registry's standard message
 * and retryability, plus whatever the EHR said (`detail`), fields it named, and a
 * retry-after hint. Pass `message` only to be more specific than the standard text
 * in a way that is still safe to show staff (no PHI, no EHR internals).
 */
export function buildConnectorError(
  code: EhrErrorCode,
  opts: { message?: string; detail?: string; fields?: string[]; retryAfterSeconds?: number } = {}
): ConnectorError {
  const def = ehrErrorDefinition(code);
  return {
    code: def.code,
    message: opts.message ?? def.message,
    retryable: def.retryable,
    ...(opts.detail ? { detail: opts.detail } : {}),
    ...(opts.fields && opts.fields.length > 0 ? { fields: opts.fields } : {}),
    ...(opts.retryAfterSeconds !== undefined ? { retryAfterSeconds: opts.retryAfterSeconds } : {}),
  };
}
